/**
 * <roro-gift-certificate> — landing del certificado regalo
 * (`sections/roro-gift-certificate.liquid`, /pages/gift-certificate).
 *
 * El HTML ya funciona sin esto: los importes son radios `name="id"` de un
 * formulario que va a `/cart/add`. Aquí se añade lo que lo hace agradable:
 *
 * · El importe elegido se escribe en el certificado de la foto (con un cambio
 *   animado y un brillo) y en la barra pinchada.
 * · Añadir por `/cart/add.js` y abrir el cajón con `cart:update`, igual que el
 *   resto del tema: `RoroCart.post` + `RoroCart.cartAfterAdd`, sección
 *   `roro-cart-drawer-render` (ver `rules/liquid-patterns.md`, «Actualizar el
 *   contador del carrito desde código propio»).
 * · Barra pinchada abajo cuando el botón principal se ha ido por arriba.
 * · Pestañas «Я дарую / Мені подарували» con teclado (flechas, Inicio, Fin).
 * · «Що можна обрати на N ₴»: piezas que cubre el importe, pedidas a la vista
 *   JSON `?view=set-picker` de la colección con el filtro de precio de la
 *   tienda. Si el importe cubre menos de 4, se completan con las más baratas de
 *   encima marcando la doplata. Una petición por importe, guardada en memoria.
 * · En escritorio con ratón, el certificado se inclina un poco hacia el cursor.
 *
 * Toda animación respeta `prefers-reduced-motion`.
 */
(function () {
  'use strict';

  if (customElements.get('roro-gift-certificate')) return;

  var IDEAS_MAX = 8;
  var IDEAS_MIN_COVERED = 4;
  var SKELETON_COUNT = 4;
  var DONE_MS = 2500;
  var SHINE_MS = 1000;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Formatea céntimos con el `shop.money_format` de la tienda, como el
   * filtro `money` de Liquid (mismo criterio que `roro-gift-pack-builder.js`).
   */
  function formatMoney(cents, format) {
    var value = Number(cents) / 100;
    if (!isFinite(value)) value = 0;

    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      var thousands = ',';
      var decimal = '.';
      var digits = 2;

      switch (token) {
        case 'amount_no_decimals':
          digits = 0;
          break;
        case 'amount_with_comma_separator':
          thousands = '.';
          decimal = ',';
          break;
        case 'amount_no_decimals_with_comma_separator':
          thousands = '.';
          digits = 0;
          break;
        case 'amount_no_decimals_with_space_separator':
          thousands = ' ';
          digits = 0;
          break;
        case 'amount_with_space_separator':
          thousands = ' ';
          decimal = ',';
          break;
        case 'amount_with_period_and_space_separator':
          thousands = ' ';
          break;
        case 'amount_with_apostrophe_separator':
          thousands = "'";
          break;
      }

      var parts = value.toFixed(digits).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    });
  }

  /** Quita y vuelve a poner una clase para que su animación arranque otra vez. */
  function restartClass(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    el.addEventListener('animationend', function done(event) {
      if (event.target !== el) return;
      el.classList.remove(cls);
      el.removeEventListener('animationend', done);
    });
  }

  function make(tag, className) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    return el;
  }

  /**
   * Una página de la vista `set-picker`. Nunca rechaza: si algo falla, lista
   * vacía y el bloque de ideas se esconde.
   *
   * Sin cabecera `Accept: application/json`: con ella Shopify ignora `view` y
   * devuelve su JSON nativo de la colección (comprobado el 2026-10-07).
   */
  function getProducts(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        try {
          var data = JSON.parse(text);
          return data && Array.isArray(data.products) ? data.products : [];
        } catch (e) {
          return [];
        }
      })
      .catch(function () {
        return [];
      });
  }

  class RoroGiftCertificate extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-gc-form]');
      if (!this.form || this._connected) return;
      this._connected = true;

      this.stage = this.querySelector('[data-gc-stage]');
      this.cert = this.querySelector('[data-gc-cert]');
      this.amountEl = this.querySelector('[data-gc-amount]');
      this.priceEl = this.querySelector('[data-gc-price]');
      this.submitBtn = this.querySelector('[data-gc-submit]');
      this.submitLabel = this.querySelector('[data-gc-submit-label]');
      this.errorEl = this.querySelector('[data-gc-error]');
      this.sticky = this.querySelector('[data-gc-sticky]');
      this.stickyAmount = this.querySelector('[data-gc-sticky-amount]');
      this.stickyBtn = this.querySelector('[data-gc-sticky-add]');
      this.ideas = this.querySelector('[data-gc-ideas]');
      this.ideasTitle = this.querySelector('[data-gc-ideas-title]');
      this.ideasList = this.querySelector('[data-gc-ideas-list]');

      this.busy = false;
      this.ideasCache = {};
      this.ideasToken = 0;
      this.ideasArmed = false;
      this.timers = [];

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onStickyAdd = this.onStickyAdd.bind(this);

      this.form.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit);
      if (this.stickyBtn) this.stickyBtn.addEventListener('click', this.onStickyAdd);

      this.classList.add('is-ready');
      this.refreshDate();
      this.setupTabs();
      this.setupSticky();
      this.setupIdeas();
      this.setupTilt();
      this.later(this.shine.bind(this), 700);
    }

    disconnectedCallback() {
      this._connected = false;
      if (this.form) {
        this.form.removeEventListener('change', this.onChange);
        this.form.removeEventListener('submit', this.onSubmit);
      }
      if (this.stickyBtn) this.stickyBtn.removeEventListener('click', this.onStickyAdd);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.ideasObserver) this.ideasObserver.disconnect();
      if (this.teardownTilt) this.teardownTilt();
      this.timers.forEach(clearTimeout);
      this.timers = [];
    }

    later(fn, ms) {
      this.timers.push(setTimeout(fn, ms));
    }

    selected() {
      return this.form.querySelector('input[name="id"]:checked');
    }

    /* --------------------------------------------------------- Importe */

    onChange(event) {
      var input = event.target;
      if (!input || input.name !== 'id') return;

      var amount = input.dataset.amount || '';
      if (this.amountEl && this.amountEl.textContent !== amount) {
        this.amountEl.textContent = amount;
        if (!reducedMotion.matches) {
          restartClass(this.amountEl, 'is-swapping');
          restartClass(this.cert, 'is-popping');
          this.shine();
        }
      }
      if (this.stickyAmount) this.stickyAmount.textContent = amount;
      if (this.priceEl) this.priceEl.textContent = formatMoney(input.dataset.cents, this.dataset.moneyFormat);

      this.hideError();
      this.loadIdeas(input);
    }

    shine() {
      if (!this.cert || reducedMotion.matches) return;
      var cert = this.cert;
      cert.classList.remove('is-shining');
      void cert.offsetWidth;
      cert.classList.add('is-shining');
      clearTimeout(this.shineTimer);
      this.shineTimer = setTimeout(function () {
        cert.classList.remove('is-shining');
      }, SHINE_MS);
    }

    /** La fecha del certificado es la del día de quien mira, no la de la caché. */
    refreshDate() {
      var dateEl = this.querySelector('[data-gc-date]');
      var months = (this.dataset.months || '').split(',');
      if (!dateEl || months.length !== 12) return;
      var now = new Date();
      dateEl.textContent = now.getDate() + ' ' + months[now.getMonth()] + ' ' + now.getFullYear();
    }

    /* --------------------------------------------------------- Carrito */

    onSubmit(event) {
      event.preventDefault();
      this.add(this.submitBtn);
    }

    onStickyAdd() {
      this.add(this.stickyBtn);
    }

    add() {
      var input = this.selected();
      if (this.busy || !input) return;

      // Sin el ayudante del carrito, el formulario de siempre: `/cart/add`.
      if (!window.RoroCart) {
        this.form.submit();
        return;
      }

      this.busy = true;
      this.setBusy(true);
      this.hideError();

      var sectionIds = Array.prototype.map
        .call(document.querySelectorAll('cart-items-component[data-section-id]'), function (el) {
          return el.dataset.sectionId;
        })
        .filter(Boolean);

      var body = { items: [{ id: Number(input.value), quantity: 1 }] };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      var self = this;
      window.RoroCart.post(this.dataset.addUrl || '/cart/add.js', body)
        .then(function (result) {
          if (!result.ok) {
            self.showError(window.RoroCart.message(result.data, null, self.dataset.error));
            return null;
          }
          return window.RoroCart.cartAfterAdd(result).then(function (cart) {
            self.notifyCart(cart, result.data.sections);
            self.flashDone();
          });
        })
        .catch(function () {
          self.showError(self.dataset.error);
        })
        .then(function () {
          self.busy = false;
          self.setBusy(false);
        });
    }

    setBusy(on) {
      [this.submitBtn, this.stickyBtn].forEach(function (btn) {
        if (!btn) return;
        if (on) btn.setAttribute('aria-busy', 'true');
        else btn.removeAttribute('aria-busy');
      });
    }

    /** Mismo contrato que el resto del tema: el cajón se abre con este evento. */
    notifyCart(cart, sections) {
      var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || undefined,
            sourceId: 'roro-gift-certificate',
            data: {
              source: 'roro-gift-certificate',
              itemCount: count,
              didError: false,
              sections: sections
            }
          }
        })
      );
    }

    flashDone() {
      var label = this.submitLabel;
      var done = this.dataset.labelAdded;
      if (!label || !done) return;
      if (!this.idleLabel) this.idleLabel = label.textContent;
      label.textContent = done;
      var idle = this.idleLabel;
      clearTimeout(this.doneTimer);
      this.doneTimer = setTimeout(function () {
        label.textContent = idle;
      }, DONE_MS);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.error || '';
      this.errorEl.hidden = false;
      // Si el fallo llegó desde la barra pinchada, el aviso está arriba.
      if (this.sticky && this.sticky.dataset.stuck === 'true') {
        this.errorEl.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /* -------------------------------------------------- Barra pinchada */

    setupSticky() {
      if (!this.sticky || !this.submitBtn || !('IntersectionObserver' in window)) return;
      var sticky = this.sticky;
      this.stickyObserver = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        var stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.dataset.stuck = stuck ? 'true' : 'false';
      });
      this.stickyObserver.observe(this.submitBtn);
    }

    /* --------------------------------------------------------- Pestañas */

    setupTabs() {
      var tablist = this.querySelector('[data-gc-tabs]');
      if (!tablist) return;
      var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
      var panels = tabs.map(function (tab) {
        return document.getElementById(tab.getAttribute('aria-controls'));
      });
      if (!tabs.length || panels.indexOf(null) !== -1) return;

      function select(index, focus) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          panels[i].hidden = !on;
        });
        if (focus) tabs[index].focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(i, false);
        });
      });

      tablist.addEventListener('keydown', function (event) {
        var current = tabs.indexOf(document.activeElement);
        if (current === -1) return;
        var next = null;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        select(next, true);
      });

      tablist.hidden = false;
      select(0, false);
    }

    /* ------------------------------------------- Що можна обрати на N ₴ */

    setupIdeas() {
      if (!this.ideas || !this.ideasList || !this.dataset.ideasUrl || !window.fetch) return;

      this.ideas.hidden = false;
      this.renderSkeleton();

      var self = this;
      var arm = function () {
        self.ideasArmed = true;
        self.loadIdeas(self.selected());
      };

      if (!('IntersectionObserver' in window)) {
        arm();
        return;
      }

      this.ideasObserver = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          self.ideasObserver.disconnect();
          self.ideasObserver = null;
          arm();
        },
        { rootMargin: '400px 0px' }
      );
      this.ideasObserver.observe(this.ideas);
    }

    ideasUrl(params) {
      var base = this.dataset.ideasUrl;
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'view=set-picker&' + params;
    }

    /**
     * Las piezas que cubre el importe, las más vendidas primero (2026-10-27,
     * Max: antes de la más cara a la más barata, y a ₴1,500 salían cuatro
     * pendientes casi iguales). Si son menos de `IDEAS_MIN_COVERED`, las más
     * baratas de encima. El filtro de precio va en unidades de la moneda, no en
     * céntimos.
     */
    fetchIdeas(cents) {
      if (this.ideasCache[cents]) return this.ideasCache[cents];

      var units = Math.floor(cents / 100);
      var self = this;
      var promise = getProducts(this.ideasUrl('filter.v.price.lte=' + units + '&sort_by=best-selling')).then(
        function (covered) {
          covered = covered.slice(0, IDEAS_MAX);
          if (covered.length >= IDEAS_MIN_COVERED) return covered;

          return getProducts(
            self.ideasUrl('filter.v.price.gte=' + (units + 1) + '&sort_by=price-ascending')
          ).then(function (above) {
            var seen = {};
            covered.forEach(function (entry) {
              if (entry.product) seen[entry.product.id] = true;
            });
            above = above.filter(function (entry) {
              return entry.product && !seen[entry.product.id];
            });
            return covered.concat(above.slice(0, IDEAS_MAX - covered.length));
          });
        }
      );

      this.ideasCache[cents] = promise;
      return promise;
    }

    loadIdeas(input) {
      if (!this.ideasArmed || !input || !this.ideasList) return;

      var cents = Number(input.dataset.cents) || 0;
      var amount = input.dataset.amount || '';
      if (this.ideasTitle && this.dataset.ideasTitle) {
        this.ideasTitle.textContent = this.dataset.ideasTitle.replace('%amount%', amount);
      }

      var token = ++this.ideasToken;
      var self = this;
      if (!this.ideasCache[cents]) this.renderSkeleton();

      this.fetchIdeas(cents).then(function (items) {
        if (token !== self.ideasToken) return;
        // Vacío solo si falló la petición o la colección no tiene nada a la
        // venta (siempre se completa con piezas de encima): el bloque sobra.
        if (!items.length) {
          self.ideas.hidden = true;
          self.ideasList.replaceChildren();
          self.ideasList.setAttribute('aria-busy', 'false');
          delete self.ideasCache[cents];
          return;
        }
        self.ideas.hidden = false;
        self.renderIdeas(items, cents);
      });
    }

    renderSkeleton() {
      var frag = document.createDocumentFragment();
      for (var i = 0; i < SKELETON_COUNT; i++) {
        var li = make('li', 'roro-gc__idea roro-gc__idea--skeleton');
        li.setAttribute('aria-hidden', 'true');
        li.appendChild(make('span', 'roro-gc__idea-media'));
        li.appendChild(make('span', 'roro-gc__idea-title'));
        li.appendChild(make('span', 'roro-gc__idea-price'));
        frag.appendChild(li);
      }
      this.ideasList.replaceChildren(frag);
      this.ideasList.setAttribute('aria-busy', 'true');
    }

    renderIdeas(items, cents) {
      var format = this.dataset.moneyFormat;
      var topupText = this.dataset.ideasTopup || '';
      var frag = document.createDocumentFragment();

      items.forEach(function (entry) {
        var product = entry.product;
        if (!product || !product.url) return;

        var li = make('li', 'roro-gc__idea');
        var link = make('a', 'roro-gc__idea-link');
        link.href = product.url;

        var media = make('span', 'roro-gc__idea-media');
        if (entry.thumb) {
          var img = make('img');
          img.src = entry.thumb;
          // La miniatura llega a 320px; en escritorio la tarjeta mide ~280px y en
          // pantalla retina pide el doble. Misma URL del CDN con otro `width`.
          if (/[?&]width=\d+/.test(entry.thumb)) {
            img.srcset = entry.thumb + ' 320w, ' + entry.thumb.replace(/([?&])width=\d+/, '$1width=640') + ' 640w';
            img.sizes = '(min-width: 990px) 280px, (min-width: 750px) 31vw, 45vw';
          }
          img.alt = '';
          img.width = 320;
          img.height = 400;
          img.loading = 'lazy';
          img.decoding = 'async';
          media.appendChild(img);
        }

        var topup = Number(product.priceCents) - cents;
        if (topup > 0 && topupText) {
          var badge = make('span', 'roro-gc__idea-badge');
          badge.textContent = topupText.replace('%amount%', formatMoney(topup, format));
          media.appendChild(badge);
        }

        var title = make('span', 'roro-gc__idea-title');
        title.textContent = product.title || '';

        var price = make('span', 'roro-gc__idea-price');
        price.textContent = formatMoney(product.priceCents, format);

        link.appendChild(media);
        link.appendChild(title);
        link.appendChild(price);
        li.appendChild(link);
        frag.appendChild(li);
      });

      this.ideasList.replaceChildren(frag);
      this.ideasList.setAttribute('aria-busy', 'false');
      this.ideasList.scrollLeft = 0;
    }

    /* ---------------------------------------------- Inclinación (ratón) */

    setupTilt() {
      if (!this.stage || !this.cert || reducedMotion.matches) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      var stage = this.stage;
      var cert = this.cert;
      var frame = 0;
      var x = 0;
      var y = 0;

      function paint() {
        frame = 0;
        cert.style.setProperty('--ry', (x * 10).toFixed(2) + 'deg');
        cert.style.setProperty('--rx', (y * -8).toFixed(2) + 'deg');
      }

      function onMove(event) {
        if (event.pointerType !== 'mouse') return;
        var rect = stage.getBoundingClientRect();
        x = (event.clientX - rect.left) / rect.width - 0.5;
        y = (event.clientY - rect.top) / rect.height - 0.5;
        if (!frame) frame = requestAnimationFrame(paint);
      }

      function onLeave() {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        cert.style.setProperty('--rx', '0deg');
        cert.style.setProperty('--ry', '0deg');
      }

      stage.addEventListener('pointermove', onMove);
      stage.addEventListener('pointerleave', onLeave);
      this.teardownTilt = function () {
        stage.removeEventListener('pointermove', onMove);
        stage.removeEventListener('pointerleave', onLeave);
        if (frame) cancelAnimationFrame(frame);
      };
    }
  }

  customElements.define('roro-gift-certificate', RoroGiftCertificate);
})();
